import { useCallback } from 'react'
import { unwrapResult } from '@reduxjs/toolkit'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { deleteAttachment } from '../../../../store/attachments/api'
import { ServiceConfig } from '@sales-center/common'
import { get } from '@user/store/services/crud'
import { toast } from '@sales-center/components'

export function useAttachmentData() {
  const dispatch = useAppDispatch()

  const handleDeleteScAttachment = useCallback(
    async (deleteId: string) => {
      try {
        const result = await dispatch(deleteAttachment(deleteId))
        unwrapResult(result)
      } catch (error) {}
    },
    [dispatch],
  )

  const handleDownloadScAttachment = useCallback(
    async (id: string, fileName: string, isCompletedFormPdf: boolean) => {
      try {
        const response = isCompletedFormPdf
          ? await get(`${ServiceConfig.baseAPIUrl}/proposals/form/${id}/pdf`)
          : await get(`${ServiceConfig.baseAPIUrl}/attachments/download/${id}`)

        if (response.ok && response instanceof Response) {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = fileName
          document.body.appendChild(a)
          a.click()
          a.remove()
        }
      } catch (error) {
        toast.error('Error downloading file')
      }
    },
    [],
  )

  const handleDownloadLocationAttachment = useCallback(
    async (id: string, fileName: string, isCompletedFormPdf: boolean) => {
      try {
        const response = isCompletedFormPdf
          ? await get(`${ServiceConfig.baseAPIUrl}/proposals/form/${id}/pdf`)
          : await get(
              `${ServiceConfig.baseAPIUrl}/location/core/document/${id}`,
            )
        if (response.ok && response instanceof Response) {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = fileName
          document.body.appendChild(a)
          a.click()
          a.remove()
        }
      } catch (error) {
        toast.error('Error downloading file')
      }
    },
    [],
  )

  function getMimeTypeFromFileName(name: string) {
    const defaultMimeType = 'default'

    if (name) {
      const fileExtension = name?.split('.')?.pop()?.toLowerCase()

      const mimeTypes = {
        xls: 'application/vnd.ms-excel',
        doc: 'application/msword',
        txt: 'text/plain',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        rtf: 'application/rtf',
        pdf: 'application/pdf',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        jpe: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        tiff: 'image/tiff',
        tif: 'image/tif',
        psd: 'image/psd',
        raw: 'image/raw',
        bmp: 'image/bmp',
        heif: 'image/heif',
        jp2: 'image/jp2',
        j2k: 'image/j2k',
        svg: 'image/svg+xml',
        svgz: 'image/svgz',
        ai: 'image/ai',
        eps: 'image/eps',
        jpf: 'image/jpf',
        jpx: 'image/jpx',
        jpm: 'image/jpm',
        mj2: 'image/mj2',
        heic: 'image/heic',
        dib: 'image/dib',
        arw: 'image/arw',
        cr2: 'image/cr2',
        nrw: 'image/nrw',
        k25: 'image/k25',
        mov: 'video/quicktime',
        mp4: 'video/mp4',
      }
      if (fileExtension) {
        return mimeTypes[fileExtension] || defaultMimeType
      }
    }
    return defaultMimeType
  }

  return {
    handleDeleteScAttachment,
    handleDownloadScAttachment,
    getMimeTypeFromFileName,
    handleDownloadLocationAttachment,
  }
}
